import React from 'react';
import Transcript from '../../components/Trainer/Transcript/TrainerTranscript';
import { copyToClipboard, onTranscriptCopyButtonClick, toggleTranscript } from '../../store/actions/trainer';
import useLocalization from '../../localization/useLocalization';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store/types';
import type { PhraseGroup } from '../../components/Trainer/types/detection';
import type { BlacklistPhraseItem, PhraseItem, TranscriptItem } from '../../utils/transcript';

function toTranscriptItems(value: TranscriptItem[] | undefined): TranscriptItem[] {
    return value ?? [];
}

function toPhraseItems(value: PhraseGroup[]): PhraseItem[] {
    return value
        .map(item => {
            const text = typeof item.text === 'string' ? item.text : '';
            const alternatives = Array.isArray(item.alternatives)
                ? item.alternatives.map(alt => ({ text: typeof alt.text === 'string' ? alt.text : '' })).filter(alt => Boolean(alt.text))
                : [];
            const complements = Array.isArray(item.complements)
                ? item.complements
                      .map(comp => ({ text: typeof comp.text === 'string' ? comp.text : '' }))
                      .filter(comp => Boolean(comp.text))
                : undefined;

            return {
                text,
                alternatives,
                complements
            };
        })
        .filter(item => Boolean(item.text) && item.alternatives.length > 0);
}

function toBlacklistItems(value: PhraseGroup[]): BlacklistPhraseItem[] {
    return value.map(item => ({ text: typeof item.text === 'string' ? item.text : '' })).filter(item => Boolean(item.text));
}

function TranscriptContainer() {
    const dispatch = useAppDispatch();
    const localization = useLocalization();

    const stateProps = useAppSelector(({ transcript, trainer }: RootState) => {
        const {
            displayTranscript = false,
            displayTranscriptConfidence = false,
            displayTranscriptPronunciation = false
        } = trainer.userInterfaceConfig;

        return {
            transcript: toTranscriptItems(transcript.timelineTranscripts),
            isVisible: transcript.isVisible,
            copyButtonEnabled: transcript.copyEnabled,
            whitelist: toPhraseItems(trainer.whitelist),
            blacklist: toBlacklistItems(trainer.blacklist),
            showMicrophoneTranscriptOnly: !trainer.systemSoundRecognition,
            displayTranscript: Boolean(displayTranscript),
            showConfidence: Boolean(displayTranscriptConfidence),
            showPronunciation: Boolean(displayTranscriptPronunciation),
            isCallStarted: trainer.isStarted
        };
    });

    return (
        <Transcript
            transcript={stateProps.transcript}
            isVisible={stateProps.isVisible}
            copyButtonEnabled={stateProps.copyButtonEnabled}
            whitelist={stateProps.whitelist}
            blacklist={stateProps.blacklist}
            showMicrophoneTranscriptOnly={stateProps.showMicrophoneTranscriptOnly}
            displayTranscript={stateProps.displayTranscript}
            showConfidence={stateProps.showConfidence}
            showPronunciation={stateProps.showPronunciation}
            isCallStarted={stateProps.isCallStarted}
            dataTestId="trainer-transcript-section"
            localization={localization}
            onToggle={(isVisible: boolean) => dispatch(toggleTranscript(isVisible))}
            onCopyButtonClick={() => dispatch(onTranscriptCopyButtonClick())}
            copyToClipboard={(payload: string) => dispatch(copyToClipboard(payload))}
        />
    );
}

export default TranscriptContainer;
